import { PYTHON_API_BASE_URL } from "../../../services/api/apiConstants";

// The signed-in user's own account (Settings). Django calls use fetch with
// PYTHON_API_BASE_URL, as the sibling modules in features/users/api do.

function buildHeaders({ accessToken, userId }) {
  return {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
    "X-User-ID": userId || "",
  };
}

const MESSAGE_ONLY_KEYS = ["detail", "non_field_errors"];

function firstMessage(value) {
  if (Array.isArray(value)) return firstMessage(value[0]);
  return typeof value === "string" ? value : null;
}

// Turns a failed reply into one readable line. Handles users/update's
// {"error": {"message"}}, the self endpoint's {"error": "<string>"}, a DRF
// 400 field dict ({"phone": ["..."]}), and a non-JSON body (a 500 page).
async function readErrorMessage(response) {
  const fallback = `Couldn't save your details (${response.status}).`;
  let data;
  try {
    data = JSON.parse(await response.text());
  } catch {
    return fallback;
  }
  if (!data || typeof data !== "object") return fallback;

  if (typeof data.error?.message === "string" && data.error.message) {
    return data.error.message;
  }
  if (typeof data.error === "string" && data.error) {
    return data.error;
  }
  for (const [key, value] of Object.entries(data)) {
    const message = firstMessage(value);
    if (message) {
      return MESSAGE_ONLY_KEYS.includes(key) ? message : `${key}: ${message}`;
    }
  }
  return fallback;
}

async function throwReadableError(response) {
  const error = new Error(await readErrorMessage(response));
  error.status = response.status;
  throw error;
}

// GET /api/users/: the caller's own record (an object, or an array whose
// first element is the record). Returns null when there is none.
export async function fetchOwnAccount({ accessToken, userId }) {
  const response = await fetch(`${PYTHON_API_BASE_URL}/users/`, {
    method: "GET",
    headers: buildHeaders({ accessToken, userId }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      `GET /users/ failed: ${response.status} ${response.statusText} — ${body}`
    );
  }

  const data = await response.json();
  if (Array.isArray(data)) return data[0] ?? null;
  return data && typeof data === "object" ? data : null;
}

// PUT /api/users/: edits the caller's own row (the server uses the token, so
// no user_id). Any role may call it. Returns the flat user record.
export async function updateOwnAccount({ accessToken, userId, fields }) {
  const response = await fetch(`${PYTHON_API_BASE_URL}/users/`, {
    method: "PUT",
    headers: buildHeaders({ accessToken, userId }),
    body: JSON.stringify(fields),
  });

  if (!response.ok) await throwReadableError(response);
  return response.json();
}

// PUT /api/users/update/: edits a user by id (Staff only; refuses a
// Customer). Returns the reply's user record.
export async function updateUserById({ accessToken, userId, targetUserId, fields }) {
  const response = await fetch(`${PYTHON_API_BASE_URL}/users/update/`, {
    method: "PUT",
    headers: buildHeaders({ accessToken, userId }),
    body: JSON.stringify({ user_id: targetUserId, ...fields }),
  });

  if (!response.ok) await throwReadableError(response);
  const data = await response.json();
  return data?.user ?? data;
}
